// src/components/GroupEventModal.js
// Updated: 09/04/2025 08:33 PM

import React, { useState, useEffect } from 'react';
import {
  Modal, Box, Typography, TextField, Button, Switch, FormControlLabel,
  Grid, IconButton, Select, MenuItem, InputLabel, FormControl, Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RRule } from 'rrule';
import { LocalizationProvider, DateTimePicker, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../context/AuthContext';
import { format as formatFns, addDays as dateFnsAddDays } from 'date-fns';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 600,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
  borderRadius: '8px',
};

const DAYS_OF_WEEK = [
  { label: 'Sunday', value: RRule.SU },
  { label: 'Monday', value: RRule.MO },
  { label: 'Tuesday', value: RRule.TU },
  { label: 'Wednesday', value: RRule.WE },
  { label: 'Thursday', value: RRule.TH },
  { label: 'Friday', value: RRule.FR },
  { label: 'Saturday', value: RRule.SA },
];

function GroupEventModal({ open, onClose, onSave, onDelete, event, isLeadership, selectedOccurrenceStart, churchProfile }) {
  const { userProfile } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startAt, setStartAt] = useState(null);
  const [endAt, setEndAt] = useState(null);
  const [isAllDay, setIsAllDay] = useState(false);
  const [virtualLink, setVirtualLink] = useState('');

  // Recurrence State
  const [isRepeating, setIsRepeating] = useState(false);
  const [repeatFrequency, setRepeatFrequency] = useState('weekly');
  const [repeatDaysOfWeek, setRepeatDaysOfWeek] = useState([]);
  const [repeatEndsAt, setRepeatEndsAt] = useState(null);
  const [monthlyWeek, setMonthlyWeek] = useState(0);

  useEffect(() => {
    if (event) {
      setTitle(event.title || '');
      setDescription(event.description || '');
      setLocation(event.location || '');
      setStartAt(event.startDateTime || null);
      setEndAt(event.endDateTime || null);
      setIsAllDay(event.isAllDay || false);
      setVirtualLink(event.virtualLink || '');

      // Initialize recurrence states
      if (event.recurrenceRules && event.recurrenceRules.length > 0) {
        setIsRepeating(true);
        try {
          const rule = RRule.fromString(event.recurrenceRules[0]);
          const freqMap = { 0: 'yearly', 1: 'monthly', 2: 'weekly', 3: 'daily' };
          if (rule.options.freq === RRule.WEEKLY && rule.options.interval === 2) {
            setRepeatFrequency('biweekly');
          } else {
            setRepeatFrequency(freqMap[rule.options.freq]);
          }
          setRepeatDaysOfWeek(rule.options.byweekday ? rule.options.byweekday : []);
          setMonthlyWeek(rule.options.bysetpos ? (Array.isArray(rule.options.bysetpos) ? rule.options.bysetpos[0] : rule.options.bysetpos) : 0);
          setRepeatEndsAt(rule.options.until || null);
        } catch(e) { console.error("Error parsing rrule for group event:", e); }
      } else {
        setIsRepeating(false);
        setRepeatFrequency('weekly');
        setRepeatDaysOfWeek([]);
        setMonthlyWeek(0);
        setRepeatEndsAt(null);
      }
    } else {
      // Reset for new event
      setTitle('');
      setDescription('');
      setLocation('');
      setStartAt(null);
      setEndAt(null);
      setIsAllDay(false);
      setIsRepeating(false);
      setRepeatFrequency('weekly');
      setRepeatDaysOfWeek([]);
      setMonthlyWeek(0);
      setRepeatEndsAt(null);
      setVirtualLink('');
    }
  }, [event, open]);

  const handleSave = async () => {
    if (!title || !startAt) {
      alert('Please fill out the Title and Start Date.');
      return;
    }
    let finalEndAt = endAt || startAt;

    if (isAllDay) {
      const start = new Date(startAt);
      start.setHours(0, 0, 0, 0);
      let end = new Date(finalEndAt);
      end.setHours(0, 0, 0, 0);
      if (end <= start) {
        end = dateFnsAddDays(start, 1);
      }
      finalEndAt = end;
    } else {
      if (finalEndAt < startAt) {
        finalEndAt = startAt;
      }
    }
    
    // ***** FIX START: Replicate the timezone-aware rrule string generation *****
    let generatedRecurrenceRules = [];
    if (isRepeating && startAt) {
        const churchTimeZone = churchProfile?.timeZone || 'America/New_York';
        // Use the DTSTART format that the backend expects
        let rrule = `DTSTART;TZID=${churchTimeZone}:${formatFns(startAt, "yyyyMMdd'T'HHmmss")}\nRRULE:FREQ=${repeatFrequency.toUpperCase()}`;

        if (repeatFrequency === 'biweekly') {
            rrule = rrule.replace('FREQ=BIWEEKLY', 'FREQ=WEEKLY;INTERVAL=2');
        }

        if (repeatFrequency === 'monthly' && monthlyWeek !== 0) {
            const startDayOfWeek = formatFns(startAt, 'EEEEEE').toUpperCase();
            rrule += `;BYDAY=${monthlyWeek}${startDayOfWeek}`;
        } else if (repeatFrequency === 'weekly') {
            // *** THIS IS THE FIX ***
            // Convert RRule weekday objects to their string representation ('MO', 'TU', etc.)
            const dayStrings = repeatDaysOfWeek.map(day => day.toString());
            
            const selectedDays = dayStrings.length > 0
                ? dayStrings.sort().join(',')
                : formatFns(startAt, 'EEEEEE').toUpperCase();
            
            if (selectedDays) {
                rrule += `;BYDAY=${selectedDays}`;
            }
            // *** END OF FIX ***
        }

        if (repeatEndsAt) {
            const untilUtc = new Date(repeatEndsAt.getTime());
            untilUtc.setUTCHours(23, 59, 59, 999);
            rrule += `;UNTIL=${formatFns(untilUtc, "yyyyMMdd'T'HHmmss'Z'")}`;
        }
        generatedRecurrenceRules.push(rrule);
    }
    // ***** FIX END *****

    const eventData = {
      title,
      description,
      location,
      startAt,
      endAt: finalEndAt,
      isAllDay,
      virtualLink,
      recurrenceRules: generatedRecurrenceRules,
      exdates: event?.exdates || [],
    };
    onSave(eventData, event?.id);
  };
  
  const handleDelete = () => {
    onDelete(event.id);
  };

  const handleDeleteOccurrence = () => {
    onDelete(event.id, selectedOccurrenceStart);
  };

  const canDeleteOccurrence = event?.recurrenceRules?.length > 0;
  
  const formatDisplayDate = (date, allDay) => {
    if (!date) return 'Not specified';
    const options = allDay ? { dateStyle: 'medium', timeZone: 'UTC' } : { dateStyle: 'medium', timeStyle: 'short' };
    return new Date(date).toLocaleString([], options);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Modal open={open} onClose={onClose}>
        <Box sx={style}>
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2" gutterBottom>
            {isLeadership && !event ? 'Create Group Event' : 'Event Details'}
          </Typography>

          {isLeadership ? (
            <>
              <TextField margin="normal" required fullWidth label="Event Title" value={title} onChange={(e) => setTitle(e.target.value)} />
              <TextField margin="normal" fullWidth label="Description" multiline rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
              <TextField margin="normal" fullWidth label="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
              <TextField margin="normal" fullWidth label="Virtual Link" value={virtualLink} onChange={(e) => setVirtualLink(e.target.value)} />

              <FormControlLabel
                control={<Checkbox checked={isAllDay} onChange={(e) => setIsAllDay(e.target.checked)} />}
                label="All-day event"
              />

              {isAllDay ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker label="Start Date" value={startAt} onChange={(newValue) => setStartAt(newValue)}
                      slots={{ textField: TextField }}
                      slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                      enableAccessibleFieldDOMStructure={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker label="End Date (Optional)" value={endAt} onChange={(newValue) => setEndAt(newValue)}
                      slots={{ textField: TextField }}
                      slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                      enableAccessibleFieldDOMStructure={false}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DateTimePicker label="Start Time" value={startAt} onChange={(newValue) => setStartAt(newValue)}
                      slots={{ textField: TextField }}
                      slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                      enableAccessibleFieldDOMStructure={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DateTimePicker label="End Time (Optional)" value={endAt} onChange={(newValue) => setEndAt(newValue)}
                      slots={{ textField: TextField }}
                      slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                      enableAccessibleFieldDOMStructure={false}
                    />
                  </Grid>
                </Grid>
              )}
              
              {/* Recurrence Section */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Recurrence</Typography>
              <FormControlLabel
                control={<Switch checked={isRepeating} onChange={(e) => setIsRepeating(e.target.checked)} />}
                label="Does this event repeat?"
              />

              {isRepeating && (
                <Box sx={{ ml: 3, borderLeft: '2px solid #eee', pl: 2 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Repeats</InputLabel>
                    <Select
                      value={repeatFrequency}
                      label="Repeats"
                      onChange={(e) => {
                        setRepeatFrequency(e.target.value);
                        setMonthlyWeek(0);
                        setRepeatDaysOfWeek([]); // Clear days on frequency change
                      }}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="biweekly">Biweekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="yearly">Annually</MenuItem>
                    </Select>
                  </FormControl>

                  {repeatFrequency === 'monthly' && (
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Week of Month</InputLabel>
                      <Select
                        value={monthlyWeek}
                        label="Week of Month"
                        onChange={(e) => setMonthlyWeek(parseInt(e.target.value))}
                      >
                        <MenuItem value={0}>Every {startAt ? formatFns(startAt, 'EEEE') : 'day'}</MenuItem>
                        <MenuItem value={1}>First {startAt ? formatFns(startAt, 'EEEE') : 'day'}</MenuItem>
                        <MenuItem value={2}>Second {startAt ? formatFns(startAt, 'EEEE') : 'day'}</MenuItem>
                        <MenuItem value={3}>Third {startAt ? formatFns(startAt, 'EEEE') : 'day'}</MenuItem>
                        <MenuItem value={4}>Fourth {startAt ? formatFns(startAt, 'EEEE') : 'day'}</MenuItem>
                        <MenuItem value={-1}>Last {startAt ? formatFns(startAt, 'EEEE') : 'day'}</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  
                  {repeatFrequency === 'weekly' && (
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Days of Week</InputLabel>
                        <Select
                          multiple
                          value={repeatDaysOfWeek}
                          onChange={(e) => setRepeatDaysOfWeek(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                          renderValue={(selected) => selected.map(day => DAYS_OF_WEEK.find(d => d.value.weekday === day.weekday)?.label).join(', ')}
                        >
                          {DAYS_OF_WEEK.map((day) => (
                            <MenuItem key={day.value.weekday} value={day.value}>{day.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                  )}

                  <DatePicker
                    label="Ends On (Optional)"
                    value={repeatEndsAt}
                    onChange={(newValue) => setRepeatEndsAt(newValue)}
                    slots={{ textField: TextField }}
                    slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                    enableAccessibleFieldDOMStructure={false}
                  />
                </Box>
              )}
            </>
          ) : ( // Read-only view
            <>
              <Typography variant="h5" component="div" sx={{ mb: 2 }}>{title}</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}><strong>Starts:</strong> {formatDisplayDate(startAt, event?.isAllDay)}</Typography>
              {endAt && <Typography variant="body1" sx={{ mb: 2 }}><strong>Ends:</strong> {formatDisplayDate(endAt, event?.isAllDay)}</Typography>}
              {location && ( <Typography variant="body1" sx={{ mb: 2 }}><strong>Location:</strong> {location}</Typography> )}
              {description && <Typography variant="body2">{description}</Typography>}
            </>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            {isLeadership && event ? (
              <Box>
                <Button onClick={handleDelete} color="error" sx={{ mr: 1 }}>Delete Series</Button>
                {canDeleteOccurrence && (
                    <Button onClick={handleDeleteOccurrence} color="warning">
                        Delete Occurrence
                    </Button>
                )}
              </Box>
            ) : <div />}
            <Box>
              <Button onClick={onClose} sx={{ mr: 1 }}>
                {isLeadership ? 'Cancel' : 'Close'}
              </Button>
              {isLeadership && <Button onClick={handleSave} variant="contained">Save</Button>}
            </Box>
          </Box>
        </Box>
      </Modal>
    </LocalizationProvider>
  );
}

export default GroupEventModal;